import { primitiveColors } from './_generated/PrimitiveColors';
import { darkColorList } from './_generated/SemanticColors-Dark';
import { lightColorList } from './_generated/SemanticColors-Light';

// Currently these are the same for both light and dark mode, but we may want to change this in the future.
const branded = {
  /** For AI components, the top-oriented start color of gradient treatments. */
  aiGradientStart: '#677FFD',
  /** For AI components, the bottom-oriented end color of gradient treatments. */
  aiGradientEnd: '#79BFF1',
};

const darkColors = { ...darkColorList, ...primitiveColors, branded };
const lightColors = { ...lightColorList, ...primitiveColors, branded };

type DesignSystemColors = typeof lightColorList &
  typeof primitiveColors & {
    /** These colors represent specific brand interactions and experiences,
     * and should be only be used in specific contexts.
     */
    branded: typeof branded;
  };

function getSemanticColors(isDarkMode: boolean): DesignSystemColors {
  return isDarkMode ? darkColors : lightColors;
}

// Maps Du Bois colors to `antd` names. This is exclusively used for theming
// Du Bois's wrapped `antd` components.
export function getAntdColors(isDarkMode: boolean) {
  const semanticColors = getSemanticColors(isDarkMode);
  return {
    bodyBackground: semanticColors.backgroundPrimary,
    textColor: semanticColors.textPrimary,
    textColorSecondary: semanticColors.textSecondary,
    primaryColor: semanticColors.blue600,
    infoColor: '#64727D',
    errorColor: semanticColors.actionDangerPrimaryBackgroundDefault,
    successColor: '#34824F',
    warningColor: '#AF5B23',
    borderColorBase: semanticColors.border,

    // Alert colors (AntD variables)
    alertErrorTextColor: semanticColors.textValidationDanger,
    alertErrorBorderColor: semanticColors.red300,
    alertErrorBgColor: semanticColors.red100,
    alertErrorIconColor: semanticColors.textValidationDanger,

    alertInfoTextColor: semanticColors.textPrimary,
    alertInfoBorderColor: semanticColors.grey300,
    alertInfoBgColor: semanticColors.backgroundSecondary,
    alertInfoIconColor: semanticColors.textSecondary,

    alertWarningTextColor: semanticColors.textValidationWarning,
    alertWarningBorderColor: semanticColors.yellow300,
    alertWarningBgColor: semanticColors.yellow100,
    alertWarningIconColor: semanticColors.textValidationWarning,

    ...(isDarkMode && {
      alertErrorBgColor: 'transparent',
      alertInfoBgColor: 'transparent',
      alertWarningBgColor: 'transparent',
    }),

    alertTextColor: 'inherit',
    alertMessageColor: 'inherit',

    spinDotDefault: semanticColors.textSecondary,
  };
}

// When deprecating a color, add it to this object with a comment explaining why it's deprecated and a link to a JIRA ticket.
// Example: `@deprecated This color supports XXXX will be removed in an upcoming release (FEINF-1234).`
const deprecatedPrimitiveColors = {
  /** @deprecated This was an alias to `primitiveColors.blue600`, please use that instead.
   * If possible, please use an appropriate semantic color, such as `actionPrimaryBackgroundDefault`. */
  primary: primitiveColors.blue600,
  /** @deprecated This was an alias to `primitiveColors.grey600`, please use that instead.
   * If possible, please use an appropriate semantic color, such as `actionPrimaryBackgroundHover`. */
  charcoal: primitiveColors.grey600,
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioInteractiveAvailable: primitiveColors.blue600,
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioInteractiveHover: '#186099',
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioInteractivePress: '#0D4F85',
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioDisabled: '#A2AEB8',
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioDefaultBorder: '#64727D',
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioDefaultBackground: '#FFFFFF',
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioInteractiveHoverSecondary: 'rgba(34, 115, 181, 0.08)',
  /** @deprecated This color supports legacy radio styles and will be removed in an upcoming release (FEINF-1674). */
  radioInteractivePressSecondary: 'rgba(34, 115, 181, 0.16)',
};

export const deprecatedSemanticColorsLight = {
  /** @deprecated Use `backgroundDanger` (FEINF-xxxx) */
  backgroundValidationDanger: lightColorList.backgroundDanger,
  /** @deprecated Use `backgroundSuccess` (FEINF-xxxx) */
  backgroundValidationSuccess: lightColorList.backgroundSuccess,
  /** @deprecated Use `backgroundWarning` (FEINF-xxxx) */
  backgroundValidationWarning: lightColorList.backgroundWarning,
  /** @deprecated Use `border` (FEINF-xxxx) */
  borderDecorative: lightColorList.border,
  /** @deprecated Use `borderDanger` (FEINF-xxxx) */
  borderValidationDanger: lightColorList.borderDanger,
  /** @deprecated Use `borderWarning` (FEINF-xxxx) */
  borderValidationWarning: lightColorList.borderWarning,
  /** @deprecated Use `tableBackgroundUnselectedHover` (FEINF-xxxx) */
  tableRowHover: lightColorList.tableBackgroundUnselectedHover,
  /** @deprecated Use `textSecondary` (FEINF-xxxx) */
  textValidationInfo: lightColorList.textSecondary,
  /** @deprecated Use `codeBackground` (FEINF-xxxx) */
  typographyCodeBg: lightColorList.codeBackground,
};

export const deprecatedSemanticColorsDark: typeof deprecatedSemanticColorsLight = {
  backgroundValidationDanger: darkColorList.backgroundDanger,
  backgroundValidationSuccess: darkColorList.backgroundSuccess,
  backgroundValidationWarning: darkColorList.backgroundWarning,
  borderDecorative: darkColorList.border,
  borderValidationDanger: darkColorList.borderDanger,
  borderValidationWarning: darkColorList.borderWarning,
  tableRowHover: darkColorList.tableBackgroundUnselectedHover,
  textValidationInfo: darkColorList.textSecondary,
  typographyCodeBg: darkColorList.codeBackground,
};

// A static list of secondary colors; these are used as a limited palette of color options in component props, i.e. `color="lime"`.
// TODO: We should split primitive colors into primary and secondary colors, and automatically generate this list.
export type SecondaryColorToken =
  | 'brown'
  | 'coral'
  | 'indigo'
  | 'lemon'
  | 'lime'
  | 'pink'
  | 'purple'
  | 'teal'
  | 'turquoise';

export type TagColorToken = `tag${Capitalize<SecondaryColorToken>}` | 'tagDefault';

export function getColors(
  isDarkMode: boolean,
): typeof deprecatedPrimitiveColors & typeof deprecatedSemanticColorsLight & DesignSystemColors {
  return {
    ...deprecatedPrimitiveColors,
    ...(isDarkMode ? deprecatedSemanticColorsDark : deprecatedSemanticColorsLight),
    ...getSemanticColors(isDarkMode),
  };
}
